import * as LabelPrimitive from '@radix-ui/react-label';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cva, type VariantProps } from 'class-variance-authority';
import { Check, ChevronDown, Minus } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

export const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({ className, ...props }, ref) => (
  <input
    ref={ref}
    className={cn(
      'flex h-9 w-full min-w-0 rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive file:border-0 file:bg-transparent file:text-sm file:font-medium',
      className,
    )}
    {...props}
  />
));
Input.displayName = 'Input';

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(
  ({ className, ...props }, ref) => (
    <textarea
      ref={ref}
      className={cn(
        'flex min-h-20 w-full rounded-md border border-input bg-card px-3 py-2 text-sm shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 disabled:opacity-50',
        className,
      )}
      {...props}
    />
  ),
);
Textarea.displayName = 'Textarea';

export const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  ({ className, children, ...props }, ref) => (
    <select
      ref={ref}
      className={cn(
        'flex h-9 w-full rounded-md border border-input bg-card px-3 py-1 text-sm shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';

export const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, ...props }, ref) => (
  <LabelPrimitive.Root ref={ref} className={cn('text-sm font-medium leading-none peer-disabled:opacity-70', className)} {...props} />
));
Label.displayName = 'Label';

export const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'peer size-4 shrink-0 rounded-[4px] border border-input bg-card shadow-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:border-primary data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground',
      className,
    )}
    {...props}
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
      {props.checked === 'indeterminate' ? <Minus className="size-3" /> : <Check className="size-3" strokeWidth={3} />}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = 'Checkbox';

export const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>
>(({ className, ...props }, ref) => (
  <SwitchPrimitive.Root
    ref={ref}
    className={cn(
      'peer inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
      className,
    )}
    {...props}
  >
    <SwitchPrimitive.Thumb className="pointer-events-none block size-4 rounded-full bg-white shadow-lg transition-transform data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0" />
  </SwitchPrimitive.Root>
));
Switch.displayName = 'Switch';

const badgeVariants = cva('inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium whitespace-nowrap', {
  variants: {
    variant: {
      default: 'border-transparent bg-primary/10 text-primary',
      secondary: 'border-transparent bg-secondary text-secondary-foreground',
      success: 'border-transparent bg-success/15 text-success',
      warning: 'border-transparent bg-warning/20 text-amber-700 dark:text-amber-300',
      destructive: 'border-transparent bg-destructive/12 text-destructive',
      outline: 'text-muted-foreground',
    },
  },
  defaultVariants: { variant: 'default' },
});

export function Badge({ className, variant, ...props }: React.HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

/*
 * Einklappbare Karten: Innerhalb von <CollapsibleCards> lassen sich Karten mit Titel über die
 * Kopfzeile auf- und zuklappen. Der Zustand wird je Bereich und Kartentitel im Browser gemerkt.
 */
type CollapseScope = { scope: string; defaultOpen: string[]; command: { open: boolean; n: number } | null };
const CollapseScopeContext = React.createContext<CollapseScope | null>(null);
const CardCollapseContext = React.createContext<{ open: boolean; collapsible: boolean; toggle: () => void; setTitle: (t: string) => void } | null>(null);

const storeKey = (scope: string, title: string) => `sp-card-${scope}-${title}`;
function readOpen(scope: string, title: string, fallback: boolean) {
  try {
    const v = localStorage.getItem(storeKey(scope, title));
    return v === null ? fallback : v === '1';
  } catch {
    return fallback;
  }
}
function writeOpen(scope: string, title: string, open: boolean) {
  try {
    localStorage.setItem(storeKey(scope, title), open ? '1' : '0');
  } catch {
    /* nur Komfort */
  }
}

export function CollapsibleCards({ scope, defaultOpen = [], className, children }: { scope: string; defaultOpen?: string[]; className?: string; children: React.ReactNode }) {
  const [command, setCommand] = React.useState<CollapseScope['command']>(null);
  const value = React.useMemo(() => ({ scope, defaultOpen, command }), [scope, defaultOpen, command]);
  return (
    <CollapseScopeContext.Provider value={value}>
      <div className={className}>
        <div className="flex justify-end gap-3 text-xs text-muted-foreground 4xl:col-span-2">
          <button type="button" className="hover:text-foreground" onClick={() => setCommand((c) => ({ open: true, n: (c?.n ?? 0) + 1 }))}>
            Alle aufklappen
          </button>
          <button type="button" className="hover:text-foreground" onClick={() => setCommand((c) => ({ open: false, n: (c?.n ?? 0) + 1 }))}>
            Alle zuklappen
          </button>
        </div>
        {children}
      </div>
    </CollapseScopeContext.Provider>
  );
}

export function Card({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const scope = React.useContext(CollapseScopeContext);
  const [title, setTitle] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(true);
  React.useEffect(() => {
    if (scope && title) setOpen(readOpen(scope.scope, title, scope.defaultOpen.includes(title)));
  }, [scope?.scope, title]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (scope?.command && title) {
      setOpen(scope.command.open);
      writeOpen(scope.scope, title, scope.command.open);
    }
  }, [scope?.command]); // eslint-disable-line react-hooks/exhaustive-deps
  const collapsible = Boolean(scope && title);
  const ctx = React.useMemo(
    () => ({
      open: !collapsible || open,
      collapsible,
      toggle: () =>
        setOpen((o) => {
          if (scope && title) writeOpen(scope.scope, title, !o);
          return !o;
        }),
      setTitle,
    }),
    [collapsible, open, scope, title],
  );
  // Zugeklappt: nur die Kopfzeile
  const visible = ctx.open ? children : React.Children.toArray(children).filter((c) => React.isValidElement(c) && c.type === CardHeader);
  return (
    <CardCollapseContext.Provider value={ctx}>
      <div className={cn('rounded-xl border bg-card text-card-foreground shadow-xs', className)} {...props}>
        {visible}
      </div>
    </CardCollapseContext.Provider>
  );
}

export function CardHeader({ className, children, onClick, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const card = React.useContext(CardCollapseContext);
  if (!card?.collapsible) {
    return (
      <div className={cn('flex flex-col gap-1 p-5 pb-3', className)} onClick={onClick} {...props}>
        {children}
      </div>
    );
  }
  return (
    <div
      className={cn('relative flex cursor-pointer select-none flex-col gap-1 p-5 pb-3 pr-10', !card.open && 'pb-5', className)}
      onClick={(e) => {
        onClick?.(e);
        // Schaltflächen in der Kopfzeile behalten ihre Funktion
        if ((e.target as HTMLElement).closest('button, a, input, select, textarea, label')) return;
        card.toggle();
      }}
      {...props}
    >
      {children}
      <button type="button" aria-expanded={card.open} aria-label={card.open ? 'Zuklappen' : 'Aufklappen'} onClick={card.toggle} className="absolute right-3 top-4 rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground">
        <ChevronDown className={cn('size-4 transition-transform', !card.open && '-rotate-90')} />
      </button>
    </div>
  );
}

export function CardTitle({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) {
  const card = React.useContext(CardCollapseContext);
  const ref = React.useRef<HTMLHeadingElement>(null);
  const setTitle = card?.setTitle;
  React.useEffect(() => {
    const text = ref.current?.textContent?.trim();
    if (setTitle && text) setTitle(text);
  }, [setTitle]);
  return <h3 ref={ref} className={cn('font-semibold leading-tight', className)} {...props} />;
}

export function CardDescription({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

export function CardContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-5 pt-0', className)} {...props} />;
}

export function CardFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex items-center justify-end gap-2 border-t px-5 py-3', className)} {...props} />;
}

export function Skeleton({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('animate-pulse rounded-md bg-muted', className)} {...props} />;
}

export function Alert({ className, variant = 'default', ...props }: React.HTMLAttributes<HTMLDivElement> & { variant?: 'default' | 'destructive' | 'success' }) {
  return (
    <div
      role="alert"
      className={cn(
        'rounded-lg border px-4 py-3 text-sm',
        variant === 'destructive' && 'border-destructive/30 bg-destructive/8 text-destructive',
        variant === 'success' && 'border-success/30 bg-success/10 text-success',
        className,
      )}
      {...props}
    />
  );
}
